import {
	EXTERNAL_ID,
	GROUP,
	IdPCommonInputFieldName,
	IdPOidcInputFieldName,
	IdPSamlInputFieldName,
	IdPXaaInputFieldName,
	Method
} from 'consts';
import {
	IAppConfig,
	IMethodConfigFields,
	IResIdPGuide,
	IUserAttributeMapping
} from 'types/api';
import { isGroupMappingDisabled } from 'utils/s4FeaturesUtils';

const OIDC_METHODS: `${Method}`[] = [Method.Oidc, Method.Xaa];

export const loadDefaultFieldsFromGuide = (
	appConfig: IAppConfig,
	method: Method,
	guideData: IResIdPGuide,
	mandatoryUserAttributes: string[]
): IMethodConfigFields => {
	const disableUserMappingCreate = !!appConfig.disableUserMappingCreate;
	const groupMappingDisabled = isGroupMappingDisabled(appConfig.features);
	// XAA (Cross-App Access / ID-JAG) is a standalone config with its own guide bucket.
	const methodGuideData =
		method === Method.Xaa
			? guideData.guides.xaa?.[method]
			: guideData.guides.sso?.[method];

	const methodConfigFields: IMethodConfigFields = {};

	const allIdPInputFields = [
		...Object.values(IdPSamlInputFieldName),
		...Object.values(IdPOidcInputFieldName),
		...Object.values(IdPXaaInputFieldName),
		...Object.values(IdPCommonInputFieldName)
	];

	for (const fieldName of allIdPInputFields) {
		/* istanbul ignore if: we load these fields separately below (legacy)  */
		if (
			fieldName === IdPCommonInputFieldName.UserAttributes ||
			fieldName === IdPCommonInputFieldName.GroupsAttribute
		) {
			continue;
		}
		/* istanbul ignore if: this is not supported yet */
		if (fieldName === IdPCommonInputFieldName.GroupsAttributeMapping) {
			continue;
		}

		if (methodGuideData?.defaults?.[fieldName] !== undefined) {
			(methodConfigFields as any)[fieldName] =
				methodGuideData.defaults[fieldName];
		}
	}

	const guideMappingDefaults = methodGuideData?.mapping?.userAttributesDefaults;

	let defaultMappings: IUserAttributeMapping[] | null = guideMappingDefaults
		? Object.entries(guideMappingDefaults).map(([idpAttr, spAttr]) => ({
				idpAttr,
				spAttr
			}))
		: null;

	// XAA is OIDC-based and its guide prefills the same subject-claim row, so the override
	// applies there too. SAML carries no externalID default and is left alone.
	const loginIDOverride = OIDC_METHODS.includes(method)
		? appConfig.oidcLoginIdMappingDefaultOverride
		: null;
	if (defaultMappings && loginIDOverride) {
		defaultMappings = [
			...defaultMappings.filter((mapping) => mapping.spAttr !== EXTERNAL_ID),
			{ idpAttr: loginIDOverride, spAttr: EXTERNAL_ID }
		];
	}

	if (defaultMappings && disableUserMappingCreate) {
		defaultMappings = defaultMappings.filter(
			(mapping) =>
				// XAA has no mandatory user attributes at all (useMandatoryUserAttributes), so
				// without this the mapping-create restriction would drop every prefill including
				// the Login ID row the mapping still requires - keep the subject-claim default.
				(method === Method.Xaa && mapping.spAttr === EXTERNAL_ID) ||
				(mapping.spAttr && mandatoryUserAttributes.includes(mapping.spAttr))
		);
	}

	methodConfigFields[IdPCommonInputFieldName.UserAttributes] = defaultMappings;
	if (!groupMappingDisabled) {
		methodConfigFields[IdPCommonInputFieldName.GroupsAttribute] = {
			idpAttr: methodGuideData?.mapping?.groupAttributeName || null,
			spAttr: GROUP
		};
	}

	return methodConfigFields;
};
